import React, { useEffect, useState, useContext } from "react";
import {
  Card,
  CardContent,
  Typography,
  Checkbox,
  TextField,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import Autocomplete from "@mui/material/Autocomplete";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import SectionTitle from "../../SectionTitle";
import {
  getClientsBySocietyId,
  assignClientToSociety,
  removeClientFromSociety,
  listClients,
} from "../../../services/societies";
import { authContext } from "../../../contexts/AuthContext";

export const AssignedClientsCard = ({ societyId, adminMode = false }) => {
  const { t } = useTranslation();
  const [clients, setClients] = useState([]);
  const [assignedClients, setAssignedClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const { auth } = useContext(authContext);

  useEffect(() => {
    if (!societyId) return;
    fetchData();
  }, [societyId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Siempre obtener los clientes ya asignados
      const assigned = await getClientsBySocietyId(societyId);

      // Si no es client → traer todos los clientes
      let allClients = [];
      if (auth.type !== "client" && adminMode) {
        allClients = await listClients();
      } else {
        // Para client → limitarse a mostrar solo los asignados
        allClients = assigned;
      }

      setClients(allClients);
      setAssignedClients(assigned.map((c) => c.id));
    } catch (err) {
      console.error("Error fetching clients:", err);
    } finally {
      setLoading(false);
    }
  };

  const toggleAssignClient = async (clientId, checked) => {
    try {
      if (checked) {
        await assignClientToSociety(clientId, societyId);
        setAssignedClients((prev) => [...prev, clientId]);
      } else {
        await removeClientFromSociety(clientId, societyId);
        setAssignedClients((prev) => prev.filter((id) => id !== clientId));
      }
    } catch (err) {
      console.error("Error toggling client assignment:", err);
    }
  };

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent>
        <SectionTitle>{t("Associated societyClients")}</SectionTitle>

        {loading ? (
          <Typography>{t("Loading...")}</Typography>
        ) : auth.type === "client" ? (
          // Si es cliente → mostrar lista simple
          <List>
            {clients.map((client) => (
              <ListItem key={client.id}>
                <ListItemText
                  primary={client.name}
                  secondary={client.license}
                />
              </ListItem>
            ))}
          </List>
        ) : (
          // Si es admin → mostrar Autocomplete con asignaciones
          <Autocomplete
            multiple
            options={clients}
            disableCloseOnSelect
            disableClearable
            getOptionLabel={(c) => `${c.name}`}
            value={clients.filter((c) => assignedClients.includes(c.id))}
            renderOption={(props, option, { selected }) => (
              <li {...props} key={option.id}>
                <Checkbox
                  icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  style={{ marginRight: 8 }}
                  checked={selected}
                  onChange={(e) =>
                    toggleAssignClient(option.id, e.target.checked)
                  }
                  disabled={!adminMode}
                />
                {option.name} ({option.license})
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("Clients")}
                placeholder={t("Select")}
              />
            )}
            ChipProps={{
              onDelete: null,
            }}
          />
        )}
      </CardContent>
    </Card>
  );
};
